import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';
import { formatChip } from '../utils/formatTime';

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function resolveTimes(mode, selectedTemplateId, customStart, customEnd, templateMap) {
  if (mode === 'pick') {
    if (selectedTemplateId == null) return null;
    const tpl = templateMap.get(selectedTemplateId);
    if (!tpl || !tpl.startTime || !tpl.endTime) return null;
    return { startTime: tpl.startTime, endTime: tpl.endTime, crossesMidnight: !!tpl.crossesMidnight, shiftTemplateId: tpl.id };
  }
  if (mode === 'custom') {
    if (!customStart || !customEnd || customStart === customEnd) return null;
    return {
      startTime: `${customStart}:00`,
      endTime: `${customEnd}:00`,
      crossesMidnight: customEnd <= customStart,
      shiftTemplateId: null,
    };
  }
  return null;
}

function OfferShiftModal({ open, shiftTemplates, onClose, onCreated }) {
  const { t, i18n } = useTranslation('scheduler');

  const [step, setStep] = useState('slot');
  const [shiftDate, setShiftDate] = useState('');
  const [mode, setMode] = useState('pick');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customError, setCustomError] = useState('');
  const [roleLabel, setRoleLabel] = useState('');

  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState(new Set());
  const selectAllRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const todayYMD = useMemo(() => toYMD(new Date()), []);
  const templateMap = useMemo(() => new Map(shiftTemplates.map((tpl) => [tpl.id, tpl])), [shiftTemplates]);

  useEffect(() => {
    if (!open) return;
    setStep('slot');
    setShiftDate(todayYMD);
    setMode('pick');
    setSelectedTemplateId(null);
    setCustomStart('');
    setCustomEnd('');
    setCustomError('');
    setRoleLabel('');
    setCandidates([]);
    setCandidatesError(false);
    setSelectedRecipientIds(new Set());
    setSubmitting(false);
    setSubmitError('');
  }, [open, todayYMD]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, submitting, onClose]);

  const fetchCandidates = useCallback(() => {
    if (!shiftDate) return;
    setCandidatesLoading(true);
    setCandidatesError(false);
    setSelectedRecipientIds(new Set());
    apiClient
      .get(`/api/shift-offers/candidates?date=${shiftDate}`)
      .then((res) => setCandidates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCandidatesError(true))
      .finally(() => setCandidatesLoading(false));
  }, [shiftDate]);

  useEffect(() => {
    if (step === 'recipients') fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const eligibleIds = useMemo(() => candidates.filter((c) => c.eligible).map((c) => c.employeeId), [candidates]);
  const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedRecipientIds.has(id));
  const someEligibleSelected = eligibleIds.some((id) => selectedRecipientIds.has(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someEligibleSelected && !allEligibleSelected;
    }
  }, [someEligibleSelected, allEligibleSelected]);

  if (!open) return null;

  const times = resolveTimes(mode, selectedTemplateId, customStart, customEnd, templateMap);
  const isPastDate = shiftDate < todayYMD;
  const slotValid = !!shiftDate && !isPastDate && !!times;

  const toggleRecipient = (employeeId) => {
    setSelectedRecipientIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const toggleSelectAllEligible = () => {
    setSelectedRecipientIds((prev) => {
      if (allEligibleSelected) {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...eligibleIds]);
    });
  };

  const handleCustomNext = (e) => {
    e.preventDefault();
    if (customStart === customEnd) {
      setCustomError(t('picker.customTimeForm.errorEqual'));
      return;
    }
    setCustomError('');
    setStep('recipients');
  };

  const summaryLine = () => {
    const dateLabel = shiftDate
      ? new Date(`${shiftDate}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' })
      : '';
    const timeLabel = times ? formatChip(times.startTime, times.endTime) : '';
    return [roleLabel.trim(), dateLabel, timeLabel].filter(Boolean).join(' · ');
  };

  const handleSubmit = async () => {
    if (!times || selectedRecipientIds.size === 0) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await apiClient.post('/api/shift-offers', {
        shiftDate,
        startTime: times.startTime,
        endTime: times.endTime,
        crossesMidnight: times.crossesMidnight,
        shiftTemplateId: times.shiftTemplateId,
        roleLabel: roleLabel.trim() || null,
        recipientEmployeeIds: [...selectedRecipientIds],
      });
      onCreated(res.data);
    } catch (err) {
      setSubmitError(err.response?.data?.message || t('offerShift.modal.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="offer-shift-title"
    >
      <div className="w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden">
        <div className="shrink-0 flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 id="offer-shift-title" className="text-lg font-semibold text-gray-900">
            {t('offerShift.modal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="ml-4 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
            aria-label={t('offerShift.modal.closeLabel')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {step === 'slot' && (
            <>
              <div>
                <label htmlFor="offer-date" className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('offerShift.modal.slot.dateLabel')}
                </label>
                <input
                  id="offer-date"
                  type="date"
                  min={todayYMD}
                  value={shiftDate}
                  onChange={(e) => setShiftDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
                />
              </div>

              <div>
                <label htmlFor="offer-role" className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('offerShift.modal.slot.roleLabel')}
                </label>
                <input
                  id="offer-role"
                  type="text"
                  maxLength={60}
                  value={roleLabel}
                  onChange={(e) => setRoleLabel(e.target.value)}
                  placeholder={t('offerShift.modal.slot.rolePlaceholder')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
                />
              </div>

              {mode === 'pick' ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-56 overflow-y-auto">
                    {shiftTemplates.map((tpl) => {
                      const isSelected = selectedTemplateId === tpl.id;
                      const chip = tpl.startTime && tpl.endTime ? formatChip(tpl.startTime, tpl.endTime) : '';
                      const color = tpl.colorHex || '#94A3B8';
                      const label = tpl.label || tpl.name || tpl.shiftName || '';
                      return (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => setSelectedTemplateId(tpl.id)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                            isSelected ? 'bg-orange-50' : ''
                          }`}
                        >
                          <span
                            className="flex-shrink-0 rounded-sm"
                            style={{ width: 12, height: 12, backgroundColor: color, border: '0.5px solid rgba(0,0,0,0.12)' }}
                          />
                          <span className="flex-1 min-w-0">
                            <span className={`block text-sm font-medium truncate ${isSelected ? 'text-orange-700' : 'text-gray-800'}`}>
                              {label}
                            </span>
                            {chip && <span className="block text-xs text-gray-400 truncate">{chip}</span>}
                          </span>
                          {isSelected && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => { setMode('custom'); setSelectedTemplateId(null); }}
                      className="w-full flex items-center px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm text-gray-600">{t('offerShift.modal.slot.customTime')}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleCustomNext} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('offerShift.modal.slot.startLabel')}</label>
                      <input
                        type="time"
                        required
                        value={customStart}
                        onChange={(e) => { setCustomStart(e.target.value); setCustomError(''); }}
                        className="w-full px-2.5 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('offerShift.modal.slot.endLabel')}</label>
                      <input
                        type="time"
                        required
                        value={customEnd}
                        onChange={(e) => { setCustomEnd(e.target.value); setCustomError(''); }}
                        className="w-full px-2.5 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
                      />
                    </div>
                  </div>
                  {customError && <p className="text-xs text-red-600" role="alert">{customError}</p>}
                  <button
                    type="button"
                    onClick={() => setMode('pick')}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700 underline underline-offset-2"
                  >
                    {t('offerShift.modal.slot.back')}
                  </button>
                </form>
              )}

              {isPastDate && (
                <p className="text-xs text-red-600" role="alert">{t('offerShift.modal.slot.errorPastDate')}</p>
              )}
            </>
          )}

          {step === 'recipients' && (
            <>
              {candidatesLoading ? (
                <div className="flex items-center justify-center py-10 text-sm text-gray-500 gap-2">
                  <Spinner />
                  {t('offerShift.modal.recipients.loading')}
                </div>
              ) : candidatesError ? (
                <div className="text-center py-10">
                  <p className="text-sm text-red-600 mb-2">{t('offerShift.modal.recipients.loadFailed')}</p>
                  <button
                    type="button"
                    onClick={fetchCandidates}
                    className="text-sm font-medium text-orange-600 hover:text-orange-700 underline underline-offset-2"
                  >
                    {t('offerShift.modal.recipients.retry')}
                  </button>
                </div>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">{t('offerShift.modal.recipients.noneEligible')}</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    {t('offerShift.modal.recipients.onlyOptedInHint')}
                  </p>

                  <div className="flex items-center justify-between px-1">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allEligibleSelected}
                        onChange={toggleSelectAllEligible}
                        disabled={eligibleIds.length === 0}
                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      {t('offerShift.modal.recipients.selectAllEligible')}
                    </label>
                    <span className="text-xs text-gray-500">
                      {t('offerShift.modal.recipients.selectedCount', { count: selectedRecipientIds.size })}
                    </span>
                  </div>

                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {candidates.map((c) => (
                      <label
                        key={c.employeeId}
                        className={`flex items-center gap-3 px-3 py-2.5 ${c.eligible ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                      >
                        <input
                          type="checkbox"
                          disabled={!c.eligible}
                          checked={selectedRecipientIds.has(c.employeeId)}
                          onChange={() => toggleRecipient(c.employeeId)}
                          className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">{c.name}</span>
                        {!c.eligible && (
                          <span className="text-xs text-gray-400 shrink-0">{t(c.ineligibleReason, { ns: 'errors' })}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">{summaryLine()}</p>
              </div>
              <p className="text-sm text-gray-700">
                {t('offerShift.modal.confirm.recipientCount', { count: selectedRecipientIds.size })}
              </p>
              <p className="text-xs text-gray-500">
                {t('offerShift.modal.confirm.costHint', { count: selectedRecipientIds.size })}
              </p>
              {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}
            </>
          )}
        </div>

        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-100">
          {step === 'slot' ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                {t('offerShift.modal.closeLabel')}
              </button>
              <button
                type="button"
                onClick={() => setStep('recipients')}
                disabled={!slotValid}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('offerShift.modal.slot.next')}
              </button>
            </>
          ) : step === 'recipients' ? (
            <>
              <button
                type="button"
                onClick={() => setStep('slot')}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                {t('offerShift.modal.recipients.back')}
              </button>
              <button
                type="button"
                onClick={() => setStep('confirm')}
                disabled={selectedRecipientIds.size === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('offerShift.modal.recipients.next')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep('recipients')}
                disabled={submitting}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                {t('offerShift.modal.confirm.back')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <Spinner />}
                {submitting ? t('offerShift.modal.confirm.sending') : t('offerShift.modal.confirm.send')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OfferShiftModal;
